/**
 * Record Pages Mock Data
 *
 * Defines default record page assignments for all objects.
 * Record pages control which page layout is shown based on application context.
 */

import { createPersistedMockData } from '../mockDataManager';
import { RecordPage } from '../../types/recordPages';

const defaultRecordPages: RecordPage[] = [
  // ============================================================
  // LEAD RECORD PAGES
  // ============================================================
  {
    id: 'rp_lead_001',
    name: 'Standard Lead Page',
    description: 'Default page layout for all Lead records',
    objectName: 'Lead__c',
    application: 'Global',
    pageLayoutId: 'layout_lead_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // CLIENT RECORD PAGES (Multiple Applications)
  // ============================================================
  {
    id: 'rp_client_001',
    name: 'Standard Client Page',
    description: 'Default page layout for all Client records',
    objectName: 'Client__c',
    application: 'Global',
    pageLayoutId: 'layout_client_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_002',
    name: 'CyberSweep Client Page',
    description: 'Client view optimized for CyberSweep monitoring',
    objectName: 'Client__c',
    application: 'CyberSweep',
    pageLayoutId: 'layout_client_cybersweep',
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_003',
    name: 'Credit Repair Client Page',
    description: 'Client view for Credit Repair services',
    objectName: 'Client__c',
    application: 'CreditRepair',
    pageLayoutId: 'layout_client_creditrepair',
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_004',
    name: 'Credit Development Client Page',
    description: 'Client view for Credit Development',
    objectName: 'Client__c',
    application: 'CreditDevelopment',
    pageLayoutId: 'layout_client_creditdevelopment',
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_005',
    name: 'Credit Block Client Page',
    description: 'Client view for Credit Block services with bureau protection details',
    objectName: 'Client__c',
    application: 'CreditBlock',
    pageLayoutId: 'layout_client_creditblock',
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_006',
    name: 'ID Theft Recovery Client Page',
    description: 'Client view for ID Theft Recovery',
    objectName: 'Client__c',
    application: 'IDTheftRecovery',
    pageLayoutId: 'layout_client_idtheftrecovery',
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  {
    id: 'rp_client_007',
    name: 'Regal Main Client Page',
    description: 'Client view for Regal Main application',
    objectName: 'Client__c',
    application: 'RegalMain',
    pageLayoutId: 'layout_client_default', // Will be replaced when app-specific layout created
    priority: 2,
    isActive: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // CONTACT RECORD PAGES
  // ============================================================
  {
    id: 'rp_contact_001',
    name: 'Standard Contact Page',
    description: 'Default page layout for all Contact records',
    objectName: 'Contact__c',
    application: 'Global',
    pageLayoutId: 'layout_contact_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // OPPORTUNITY RECORD PAGES
  // ============================================================
  {
    id: 'rp_opportunity_001',
    name: 'Standard Opportunity Page',
    description: 'Default page layout for all Opportunity records',
    objectName: 'Opportunity__c',
    application: 'Global',
    pageLayoutId: 'layout_opportunity_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // CASE RECORD PAGES
  // ============================================================
  {
    id: 'rp_case_001',
    name: 'Standard Case Page',
    description: 'Default page layout for all Case records',
    objectName: 'Case__c',
    application: 'Global',
    pageLayoutId: 'layout_case_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  {
    id: 'rp_case_credit_block',
    name: 'Credit Block Case Page',
    description: 'Case layout for Credit Block operations — shows sub-type, owner, client fields',
    objectName: 'Case__c',
    application: 'CreditBlock',
    pageLayoutId: 'layout_case_credit_block',
    priority: 10,
    isActive: true,
    isDefault: false,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // EVENT RECORD PAGES
  // ============================================================
  {
    id: 'rp_event_001',
    name: 'Standard Event Page',
    description: 'Default page layout for all Event records',
    objectName: 'Event__c',
    application: 'Global',
    pageLayoutId: 'layout_event_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // TASK RECORD PAGES
  // ============================================================
  {
    id: 'rp_task_001',
    name: 'Standard Task Page',
    description: 'Default page layout for all Task records',
    objectName: 'Task__c',
    application: 'Global',
    pageLayoutId: 'layout_task_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  {
    id: 'rp_task_credit_block',
    name: 'Credit Block Task Page',
    description: 'Task layout for Credit Block operations — shows client, bureau, and case link fields',
    objectName: 'Task__c',
    application: 'CreditBlock',
    pageLayoutId: 'layout_task_credit_block',
    priority: 10,
    isActive: true,
    isDefault: false,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },

  // ============================================================
  // REPORT RECORD PAGES
  // ============================================================
  {
    id: 'rp_report_001',
    name: 'Standard Report Page',
    description: 'Default page layout for all Report records',
    objectName: 'Report__c',
    application: 'Global',
    pageLayoutId: 'layout_report_default',
    priority: 1,
    isActive: true,
    isDefault: true,
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  }
];

/**
 * Persisted Record Pages Storage
 * Automatically saves to localStorage for persistence
 */
export const persistedRecordPages = createPersistedMockData<RecordPage>(
  'crm_record_pages',
  defaultRecordPages
);

/**
 * Helper function to get record pages for an object
 */
export function getRecordPagesByObject(objectName: string): RecordPage[] {
  const allRecordPages = persistedRecordPages.get();
  return allRecordPages
    .filter(rp => rp.objectName === objectName && rp.isActive)
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Helper function to get default record page for an object
 */
export function getDefaultRecordPage(objectName: string): RecordPage | null {
  const objectPages = getRecordPagesByObject(objectName);
  return objectPages.find(rp => rp.isDefault) || objectPages[0] || null;
}
